<template>
  <CDialog
    :visible.sync="visible"
    :title="$t('Alert setting')"
    submittable
    :submit-label="$t('Save Changes')"
    :submit-loading="loading"
    @submit="saveBudget"
    @open="resetData"
    :submitDisabled="!isAllowedBudgetSettingEdit"
  >
    <form @submit.prevent="saveBudget">
      <CTitle>{{ $t('Notification destination setting') }}</CTitle>

      <section>
        <CRow gutter="md">
          <CCol col="6">
            <div class="label">
              <label> <Icon name="envelope" type="far" margin />{{ $t('Email address') }} </label>
              <el-switch v-model="enableNotificationEmail" v-if="isAllowedBudgetSettingEdit" />
            </div>

            <CustomInput
              ref="inputEmail"
              type="email"
              v-model="emailAddress"
              :placeholder="$t('Email address')"
              :readonly="!isAllowedBudgetSettingEdit"
              :validate="{ email: true }"
              :error="emailAddressIsWrong"
              :error-msg="$t('Please enter the correct e-mail address.')"
            />
          </CCol>

          <CCol col="6">
            <div class="label">
              <label for="budgetDailyDiff">
                <Icon name="slack" type="fab" margin />{{ $t('Slack') }}
              </label>
              <el-switch v-model="enableNotificationSlack" v-if="isAllowedBudgetSettingEdit" />
            </div>

            <CustomInput
              ref="inputSlack"
              type="url"
              v-model="slackUrl"
              placeholder="Slack Web-hook URL"
              :readonly="!isAllowedBudgetSettingEdit"
              :error="slackUrlIsWrong"
              :error-msg="$t('Please enter the correct URL.')"
            />

            <div class="text-2 text-muted mt-3">
              {{ $t("Please check Slack's webhooks documentation for further information.") }}
            </div>
          </CCol>
        </CRow>
      </section>

      <CSeparator />

      <!--Budget - Daily-->
      <section v-if="isBudgetAlertEdit">
        <CTitle margin-bottom>{{ $t('Setting per day') }}</CTitle>

        <CRow gutter="md">
          <CCol col="6">
            <div class="label">
              <label for="budgetDaily">
                {{ $t('Budget for a day') }}
                <span class="ml-2 text-1">({{ $t('Dollar') }})</span>
              </label>
              <el-switch v-model="enableDaily" v-if="isAllowedBudgetSettingEdit" />
            </div>

            <div class="input-group">
              <div class="input-group-addon">$</div>
              <input
                type="number"
                class="form-control"
                placeholder="0"
                name="budgetDaily"
                id="budgetDaily"
                min="0"
                v-model="daily"
                :readonly="!isAllowedBudgetSettingEdit"
              />
            </div>
          </CCol>

          <CCol col="6">
            <div class="label">
              <label for="budgetDailyDiff">
                {{ $t('Day-over-day changes') }}
                <span class="ml-2 text-1">({{ $t('Percent') }})</span>
              </label>
              <el-switch v-model="enablePreviousDay" v-if="isAllowedBudgetSettingEdit" />
            </div>
            <div class="input-group">
              <div class="input-group-addon">%</div>
              <input
                type="number"
                class="form-control"
                placeholder="0"
                id="budgetDailyDiff"
                name="budgetDailyDiff"
                min="0"
                max="10000"
                v-model="previousDay"
                :readonly="!isAllowedBudgetSettingEdit"
              />
            </div>
          </CCol>
        </CRow>
      </section>

      <!--Budget - Monthly-->
      <section v-if="isBudgetAlertEdit">
        <CTitle margin-bottom>{{ $t('Setting per month') }}</CTitle>

        <CRow gutter="md">
          <CCol col="6">
            <div class="label">
              <label for="budgetMonthly">
                {{ $t('Budget for a month') }}
                <span class="ml-2 text-1">({{ $t('Dollar') }})</span>
              </label>
              <el-switch v-model="enableMonthly" v-if="isAllowedBudgetSettingEdit" />
            </div>

            <div class="input-group">
              <div class="input-group-addon">$</div>
              <input
                type="number"
                id="budgetMonthly"
                class="form-control"
                placeholder="0"
                name="budgetMonthly"
                min="0"
                v-model="monthly"
                :readonly="!isAllowedBudgetSettingEdit"
              />
            </div>
          </CCol>
        </CRow>
      </section>
    </form>
  </CDialog>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import CDialog from '@/components/uikit/CDialog';
import CTitle from '@/components/uikit/CTitle';
import CRow from '@/components/uikit/CRow';
import CCol from '@/components/uikit/CCol';
import CSeparator from '@/components/uikit/CSeparator';

import Icon from '@/components/uikit/Icon';
import Button from '@/components/uikit/Button';
import CustomInput from '@/components/uikit/CustomInput';

const messages = {
  en: {
    'Alert setting': 'Alert Settings',
    'Save Changes': 'Save',
    'Notification destination setting': 'Notification Settings',
    'Email address': 'Email Address',
    Slack: 'Slack',
    'Email notification': 'Email Notification',
    'Slack notification': 'Slack Notification',
    'Please enter the correct e-mail address.': 'Please enter the correct e-mail address.',
    'Please enter the correct URL.': 'Please enter the correct URL.',
    "Please check Slack's webhooks documentation for further information.":
      "Please check Slack's webhooks documentation for more information.",
    'Setting per day': 'Daily Budget Alert Settings',
    'Setting per month': 'Monthly Budget Alert Settings',
    'Budget for a day': 'Total Amount',
    'Day-over-day changes': 'Charge',
    'Budget for a month': 'Total Amount',
    'Budget was set': 'Budget was set',
    Percent: '%',
    Dollar: '$',
  },
  ja: {
    'Alert setting': 'アラートの設定',
    'Save Changes': '設定を保存',
    'Notification destination setting': '通知先の設定',
    'Email address': 'メールアドレス',
    Slack: 'Slack',
    'Email notification': 'メール通知',
    'Slack notification': 'Slack通知',
    'Please enter the correct e-mail address.': '正しいメールアドレスを入力してください。',
    'Please enter the correct URL.': '正しいURLを入力してください。',
    "Please check Slack's webhooks documentation for further information.":
      'webhookの設定に関しては公式のドキュメントを参照してください。',
    'Setting per day': '日毎の設定',
    'Setting per month': '月毎の設定',
    'Budget for a day': '１日の予算',
    'Day-over-day changes': '前日比',
    'Budget for a month': '１ヶ月の予算',
    'Budget was set': '予算の設定を変更しました',
    Percent: 'パーセント',
    Dollar: 'ドル',
  },
};

export default {
  name: 'SettingBudgetModal',
  i18n: { messages },
  components: { CDialog, CTitle, CRow, CCol, CSeparator, Icon, Button, CustomInput },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      emailAddressIsWrong: false,
      slackUrlIsWrong: false,

      emailAddress: '',
      slackUrl: '',
      enableNotificationEmail: false,
      enableNotificationSlack: false,

      daily: 0,
      previousDay: 0,
      monthly: 0,
      enableDaily: false,
      enablePreviousDay: false,
      enableMonthly: false,
    };
  },
  watch: {
    enableNotificationEmail(val) {
      if (val) {
        this.$refs['inputEmail'].focus();
      }

      this.emailAddressIsWrong = false;
    },
    enableNotificationSlack(val) {
      if (val) {
        this.$refs['inputSlack'].focus();
      }

      this.slackUrlIsWrong = false;
    },
    emailAddress() {
      this.emailAddressIsWrong = false;
    },
    slackUrl() {
      this.slackUrlIsWrong = false;
    },
  },
  computed: {
    ...mapState('accounts', {
      selectedId: state => state.selectedId,
      details: state => state.details,
    }),

    ...mapState('crossTags', {
      crossTags: state => state.crossTags.data,
    }),

    ...mapState('usageReports', {
      groupBy: state => state.groupBy,
    }),

    ...mapState({
      dataType: state => state.userState.dataType,
      dataStyle: state => state.userState.dataStyle,
    }),

    ...mapGetters('rbac', {
      isResourceAllowed: 'isResourceAllowed',
    }),

    ...mapGetters('user', {
      reportCostType: 'getReportCostType',
      costType: 'getCostType',
    }),

    budget() {
      const costTypeBudget = this.costType.replace('cost', 'budget');
      return this.details[this.selectedId][`${costTypeBudget}`];
    },

    notification() {
      const costTypeNotification = this.costType.replace('cost', 'notification');

      return this.details[this.selectedId][`${costTypeNotification}`];
    },

    isAllowedBudgetSettingEdit() {
      return (
        this.$display('usage_account_menu_budget_edit') &&
        this.$can('ModifyAccountSettings') &&
        this.isResourceAllowed(['ModifyAccountSettings'], 'account_id', this.selectedId)
      );
    },

    isBudgetAlertEdit() {
      return this.$can('ModifyAccountSettings');
    },

    visible: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
  },
  methods: {
    ...mapActions('accounts', ['fetchAccounts', 'fetchDetail']),

    async saveBudget() {
      try {
        // Check values
        if (this.enableNotificationEmail && !this.emailAddress) {
          this.emailAddressIsWrong = true;
          return false;
        }

        if (this.enableNotificationSlack && !this.slackUrl) {
          this.slackUrlIsWrong = true;
          return false;
        }

        this.loading = true;

        const params = {
          budget: {
            daily: this.daily,
            previous_day: this.previousDay,
            monthly: this.monthly,
          },
          notification: {
            daily: this.enableDaily,
            previous_day: this.enablePreviousDay,
            monthly: this.enableMonthly,
            email: {
              address: this.emailAddress,
              enabled: this.enableNotificationEmail,
            },
            slack: {
              url: this.slackUrl,
              enabled: this.enableNotificationSlack,
            },
          },
          type: this.reportCostType,
        };

        await this.putBudget(params);
        // await this.fetchAccounts({ force: true });
        await this.fetchDetail({ force: true });
        this.visible = false;
        await this.$alpha.notify.success('change', this.$t('Alert setting'));
      } catch (err) {
        if (err.response) {
          if (err.response.data.message === 'slack not specified') {
            this.slackUrlIsWrong = true;
            this.$refs['inputSlack'].focus();
          }

          if (err.response.data.message === 'notification.slack.url not specified') {
            this.slackUrlIsWrong = true;
            this.$refs['inputSlack'].focus();
          }
        }

        await this.$alpha.notify.error('change', this.$t('Alert setting'));
      } finally {
        this.loading = false;
      }
    },

    putBudget(params) {
      return this.$store.dispatch('accounts/putBudget', params);
    },

    resetData() {
      this.emailAddress = this.notification['email']['address'];
      this.slackUrl = this.notification['slack']['url'];
      this.enableNotificationEmail = this.notification['email']['enabled'];
      this.enableNotificationSlack = this.notification['slack']['enabled'];
      this.daily = this.budget['daily'];
      this.previousDay = this.budget['previous_day'];
      this.monthly = this.budget['monthly'];
      this.enableDaily = this.notification['daily'];
      this.enablePreviousDay = this.notification['previous_day'];
      this.enableMonthly = this.notification['monthly'];
    },
  },
};
</script>

<style lang="scss" scoped>
.dialog-footer {
  text-align: right;
  margin-top: 3rem;
}

.label {
  display: flex;
  justify-content: space-between;
}

label {
  display: block;
}

section {
  padding: $space-md 0;
}
</style>
